import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Icon from '../Icon';
import { isIOS } from '../../../utils/platform';

import { ReactComponent as SoundOffIcon } from '../../../icons/sound-off-solid.svg';

import './NoSoundHelp.scss';

/**
 * A "No sound?" link that opens, in place, the checklist for getting playback to be heard.
 *
 * Opens inline rather than in a dialog: every caller already sits it inside a modal, and the list
 * is short enough to read where the Play button is.
 *
 * `action` is a button to sit beside the link as a pill of the same shape, such as the mixer's Reset.
 */
const NoSoundHelp = ({ hasVolumeSliders = false, action = null }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="NoSoundHelp">
      <div className="buttons">
        <button type="button" className="toggle" aria-expanded={isOpen} onClick={() => setIsOpen((open) => !open)}>
          <Icon icon={<SoundOffIcon aria-hidden="true" />} className="solid" />
          <span className="label">{t('noSound.title')}</span>
        </button>
        {action && <span className="action">{action}</span>}
      </div>

      {/* Always rendered, so closing can slide shut as well as opening slide down */}
      <div className={`drawer${isOpen ? ' drawer--open' : ''}`} aria-hidden={!isOpen}>
        <div className="drawer-inner">
          <div className="help">
            <p className="intro">{t('noSound.intro')}</p>
            <ul className="reasons">
              {/* The ring/silent switch mutes web audio on iOS only; Android and desktop play through it */}
              {isIOS && <li>{t('noSound.silentMode')}</li>}
              <li>{t('noSound.volumeUp')}</li>
              {hasVolumeSliders && <li>{t('noSound.slidersUp')}</li>}
              <li>{t('noSound.restartBrowser')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

NoSoundHelp.propTypes = {
  /** Adds the "turn the sliders up" step, for callers that render volume sliders of their own */
  hasVolumeSliders: PropTypes.bool,
  action: PropTypes.node,
};

export default NoSoundHelp;
